import { Component } from '@theme/component';

/**
 * "How to apply" tabs.
 *
 * Markup contract (see sections/how-to-apply.liquid):
 *  - [role="tab"] buttons, each with aria-controls pointing at a [data-panel] element.
 *  - Each [data-panel] holds [data-step] items and one [data-counter] element.
 *  - [data-arrow="previous" | "next"] buttons step through the steps of the active panel.
 *
 * Only one panel and one step are visible at a time, using the `hidden` attribute, so the page
 * still shows the first tab and the first step if this script never runs.
 *
 * @extends {Component}
 */
class HowToApplyComponent extends Component {
  /** @type {number} */
  #stepIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);

    const initial = Number(this.dataset.initialTab || 1) - 1;
    const tabs = this.#tabs;
    this.#selectTab(tabs.length ? Math.min(Math.max(initial, 0), tabs.length - 1) : -1);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  /** @returns {HTMLElement[]} */
  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-panel]')]);
  }

  /** @returns {HTMLElement | undefined} */
  get #activePanel() {
    return this.#panels.find((panel) => !panel.hidden);
  }

  /**
   * @param {HTMLElement} tab
   * @returns {HTMLElement | null}
   */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? /** @type {HTMLElement | null} */ (this.querySelector(`#${CSS.escape(id)}`)) : null;
  }

  /**
   * Shows one tab and its panel. Pass -1 when there are no tab blocks to show the first panel.
   *
   * @param {number} index
   * @param {{ focus?: boolean, step?: number }} [options]
   */
  #selectTab(index, { focus = false, step = 0 } = {}) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    const activePanel = index >= 0 ? this.#panelFor(tabs[index] ?? tabs[0]) : panels[0];

    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    for (const panel of panels) panel.hidden = panel !== activePanel;

    if (focus && tabs[index]) tabs[index].focus();

    this.#selectStep(step);
  }

  /**
   * Shows one step of the active panel and updates the counter and arrows.
   *
   * @param {number} index
   */
  #selectStep(index) {
    const panel = this.#activePanel;
    if (!panel) return;

    const steps = /** @type {HTMLElement[]} */ ([...panel.querySelectorAll('[data-step]')]);
    const count = steps.length;
    this.#stepIndex = count ? ((index % count) + count) % count : 0;

    steps.forEach((step, i) => {
      step.hidden = i !== this.#stepIndex;
    });

    const counter = panel.querySelector('[data-counter]');
    if (counter) counter.textContent = String(this.#stepIndex + 1);

    for (const arrow of this.querySelectorAll('[data-arrow]')) {
      /** @type {HTMLElement} */ (arrow).hidden = count < 2;
    }
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (tab instanceof HTMLElement && this.contains(tab)) {
      this.#selectTab(this.#tabs.indexOf(tab));
      return;
    }

    const arrow = event.target.closest('[data-arrow]');
    if (arrow instanceof HTMLElement && this.contains(arrow)) {
      this.#selectStep(this.#stepIndex + (arrow.dataset.arrow === 'next' ? 1 : -1));
    }
  };

  /**
   * Arrow keys, Home and End move between tabs (roving tabindex).
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[role="tab"]')) return;

    const tabs = this.#tabs;
    const current = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = rtl ? 'ArrowRight' : 'ArrowLeft';
    let next = -1;

    if (event.key === forward) next = (current + 1) % tabs.length;
    else if (event.key === backward) next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;

    if (next === -1) return;

    event.preventDefault();
    this.#selectTab(next, { focus: true });
  };

  /**
   * In the Theme Editor, selecting a Tab or Step block in the sidebar shows it on the page.
   *
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (tab instanceof HTMLElement) {
      this.#selectTab(this.#tabs.indexOf(tab));
      return;
    }

    const step = event.target.closest('[data-step]');
    const panel = step?.closest('[data-panel]');
    if (!step || !(panel instanceof HTMLElement)) return;

    const tabIndex = this.#tabs.findIndex((candidate) => this.#panelFor(candidate) === panel);
    const stepIndex = [...panel.querySelectorAll('[data-step]')].indexOf(step);
    this.#selectTab(tabIndex, { step: stepIndex });
  };
}

if (!customElements.get('how-to-apply-component')) {
  customElements.define('how-to-apply-component', HowToApplyComponent);
}
